/**
 * Collection Sticky Add to Cart Component
 * 
 * Mobile-only sticky add to cart for collection pages that links to product pages
 * instead of directly adding to cart (since collection pages use product links)
 */

import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} CollectionStickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLElement} buttonsContainer - Container for the product buttons
 */

/**
 * A custom element that manages a sticky add-to-cart bar for collection pages.
 * Shows when the pricing section scrolls out of view on mobile devices.
 *
 * @extends {Component<CollectionStickyAddToCartRefs>}
 */
class CollectionStickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'buttonsContainer'];

  /** @type {IntersectionObserver} */
  #pricingObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {ResizeObserver} */
  #resizeObserver = null;

  constructor() {
    super();
    this.#handleScroll = this.#handleScroll.bind(this);
    this.#handleResize = this.#handleResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.isConnected) return;

    this.#setupObservers();
    this.#handleResize(); // Initial check
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupObservers();
  }

  /**
   * Sets up intersection and resize observers
   */
  #setupObservers() {
    // Only activate on mobile devices
    if (window.innerWidth >= 768) {
      this.refs.stickyBar.style.display = 'none';
      return;
    }

    // Find the pricing section to observe
    const pricingSection = document.querySelector('#subscription-plans');
    if (!pricingSection) return;

    // Set up intersection observer for pricing section
    this.#pricingObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          this.#isStuck = !entry.isIntersecting;
          this.#updateStickyState();
        });
      },
      { threshold: 0.1, rootMargin: '-80px 0px 0px 0px' }
    );

    this.#pricingObserver.observe(pricingSection);

    // Set up resize observer to handle screen size changes
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(document.body);

    // Add scroll listener for smooth transitions
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  /**
   * Cleans up observers and event listeners
   */
  #cleanupObservers() {
    if (this.#pricingObserver) {
      this.#pricingObserver.disconnect();
      this.#pricingObserver = null;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    window.removeEventListener('scroll', this.#handleScroll);
  }

  /**
   * Handles window resize events
   */
  #handleResize() {
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      this.refs.stickyBar.style.display = 'flex';
      // Re-setup observers if they were previously disconnected
      if (!this.#pricingObserver) {
        this.#setupObservers();
      }
    } else {
      this.refs.stickyBar.style.display = 'none';
      this.#cleanupObservers();
    }
  }

  /**
   * Handles scroll events for smooth transitions
   */
  #handleScroll() {
    this.#updateStickyState();
  }

  /**
   * Updates the sticky bar state based on scroll position
   */
  #updateStickyState() {
    const { stickyBar } = this.refs;
    
    if (this.#isStuck) {
      stickyBar.setAttribute('data-stuck', 'true');
      stickyBar.style.transform = 'translateY(0)';
    } else {
      stickyBar.setAttribute('data-stuck', 'false');
      stickyBar.style.transform = 'translateY(100%)';
    }
  }

  /**
   * Creates product buttons from the pricing section
   */
  #createProductButtons() {
    const pricingSection = document.querySelector('#subscription-plans');
    if (!pricingSection) return;

    // Find all product cards with links
    const productCards = pricingSection.querySelectorAll('[data-product-link]');
    const buttonsContainer = this.refs.buttonsContainer;
    
    // Clear existing buttons
    buttonsContainer.innerHTML = '';

    productCards.forEach(card => {
      const link = card.getAttribute('data-product-link');
      const label = card.getAttribute('data-product-label') || 'Subscribe';
      const featured = card.getAttribute('data-featured') === 'true';

      const button = document.createElement('a');
      button.href = link;
      button.className = `collection-sticky-button ${featured ? 'featured' : ''}`;
      button.textContent = label;
      button.setAttribute('data-product-link', link);

      buttonsContainer.appendChild(button);
    });
  }

  /**
   * Updates the component when the theme re-renders
   */
  async update() {
    await onAnimationEnd(this.refs.stickyBar);
    this.#createProductButtons();
  }
}

// Register the custom element
if (!customElements.get('collection-sticky-add-to-cart')) {
  customElements.define('collection-sticky-add-to-cart', CollectionStickyAddToCartComponent);
}